import type {
  EquipeEncarregadoDto,
  LocalCargaDto,
  LocalDescargaDto,
  MaterialDto,
  MveDto,
  NamedDto,
  NamedWithChapaDto,
  NamedWithProjetoDto,
  ObraDto,
  ProjetoDto,
  ProjetoServicoDto,
  ServicoDto,
  TrechoDto,
  UnidadeDto,
} from '@/types/dtos'
import type {
  CatApontador,
  CatDiametro,
  CatEncarregado,
  CatEquipe,
  CatEquipeEncarregado,
  CatLocalCarga,
  CatLocalDescargaRod,
  CatLocalDescargaUrb,
  CatMaterial,
  CatMaterialRede,
  CatMotorista,
  CatMve,
  CatObra,
  CatProjeto,
  CatProjetoServico,
  CatServico,
  CatTipoRede,
  CatTrecho,
  CatUnidade,
} from './entities'

/**
 * DTO (rede) → Entity (Dexie). Mantém o snapshot pattern (id+nome) idêntico ao Android
 * (`data/catalog/CatalogMappers.kt`).
 */

export function projetoDtoToEntity(d: ProjetoDto): CatProjeto {
  return { id: d.id, codigo: d.codigo }
}

export function obraDtoToEntity(d: ObraDto): CatObra {
  return {
    id: d.id,
    codigo: d.codigo,
    nome: d.nome,
    status: d.status,
    projetoId: d.projeto?.id ?? null,
    projetoCodigo: d.projeto?.codigo ?? null,
    formularioId: d.formulario?.id ?? null,
    formularioNome: d.formulario?.nome ?? null,
    formularioTp: d.formulario?.tp ?? null,
    tipoProjetoId: d.tipoProjeto?.id ?? null,
    tipoProjetoNome: d.tipoProjeto?.nome ?? null,
    chefeAdministrativoId: d.chefeAdministrativo?.id ?? null,
    chefeAdministrativoNome: d.chefeAdministrativo?.nome ?? null,
  }
}

export function servicoDtoToEntity(d: ServicoDto): CatServico {
  return {
    id: d.id,
    nome: d.nome,
    tp: d.tp,
    formulario: d.formulario ?? null,
    unidadeId: d.unidade?.id ?? null,
    unidadeNome: d.unidade?.nome ?? null,
    unidadeSigla: d.unidade?.sigla ?? null,
    materialIdDefault: d.materialIdDefault ?? null,
    materialId: d.material?.id ?? null,
    materialNome: d.material?.nome ?? null,
    materialUnidadeId: d.material?.unidadeId ?? null,
    materialUnidadeNome: d.material?.unidadeNome ?? null,
    contratoItemId: d.contratoItemId ?? null,
    contratoGarantia: d.contratoGarantia ?? null,
    contratoPreco: d.contratoPreco ?? null,
    contratoDtInicio: d.contratoDtInicio ?? null,
    contratoDtFinal: d.contratoDtFinal ?? null,
    contratoItensVigentes: d.contratoItensVigentes ?? null,
    inativo: d.inativo ?? false,
    coringa: d.coringa,
    bloqueioCubagem: d.bloqueioCubagem,
    isento: d.isento,
    flagData: d.flags.data,
    flagEquipe: d.flags.equipe,
    flagEncarregado: d.flags.encarregado,
    flagQtdExecutada: d.flags.qtdExecutada,
    flagQtdMaterial: d.flags.qtdMaterial,
    flagAlturaMedia: d.flags.alturaMedia,
    flagMaterialRede: d.flags.materialRede,
    flagDn: d.flags.dn,
    flagTpRede: d.flags.tpRede,
    flagIdentificador: d.flags.identificador,
    flagDtLancamento: d.flags.dtLancamento,
  }
}

export const namedWithChapaToEncarregado = (d: NamedWithChapaDto): CatEncarregado => ({
  id: d.id,
  nome: d.nome,
  chapa: d.chapa ?? null,
})
export const namedWithChapaToMotorista = (d: NamedWithChapaDto): CatMotorista => ({
  id: d.id,
  nome: d.nome,
  chapa: d.chapa ?? null,
})
export const namedWithChapaToApontador = (d: NamedWithChapaDto): CatApontador => ({
  id: d.id,
  nome: d.nome,
  chapa: d.chapa ?? null,
})

export const namedToEquipe = (d: NamedDto): CatEquipe => ({ id: d.id, nome: d.nome })
// Os 3 catálogos abaixo recebem `projetoId` quando o sync foi feito com escopo
// por projeto (módulo Produção). Em fetch global, o campo vem null.
export const namedToMaterialRede = (d: NamedWithProjetoDto): CatMaterialRede => ({
  id: d.id,
  nome: d.nome,
  projetoId: d.projetoId ?? null,
})
export const namedToDiametro = (d: NamedWithProjetoDto): CatDiametro => ({
  id: d.id,
  nome: d.nome,
  projetoId: d.projetoId ?? null,
})
export const namedToTipoRede = (d: NamedWithProjetoDto): CatTipoRede => ({
  id: d.id,
  nome: d.nome,
  projetoId: d.projetoId ?? null,
})

export const unidadeDtoToEntity = (d: UnidadeDto): CatUnidade => ({
  id: d.id,
  nome: d.nome,
  sigla: d.sigla ?? null,
})
export const materialDtoToEntity = (d: MaterialDto): CatMaterial => ({
  id: d.id,
  nome: d.nome,
  unidadeId: d.unidadeId ?? null,
  unidadeNome: d.unidadeNome ?? null,
})
export const trechoDtoToEntity = (d: TrechoDto): CatTrecho => ({
  id: d.id,
  nome: d.nome,
  projetoId: d.projetoId ?? null,
})
export const mveDtoToEntity = (d: MveDto): CatMve => ({
  id: d.id,
  prefixo: d.prefixo ?? null,
  // Backend já manda em UPPERCASE; reforçamos pra defender contra dados velhos
  // no cache local que tenham chegado antes do fix.
  placa: d.placa ? d.placa.toUpperCase().trim() : null,
  tp: d.tp,
  marca: d.marca ?? null,
  modelo: d.modelo ?? null,
  capacidade: d.capacidade ?? null,
  chefeEquipeId: d.chefeEquipeId ?? null,
  proprio: d.proprio === true,
})
export const localCargaDtoToEntity = (d: LocalCargaDto): CatLocalCarga => ({
  id: d.id,
  origem: d.origem ?? null,
  complemento: d.complemento ?? null,
  nome: d.nome ?? null,
  projetoId: d.projetoId ?? null,
})
export const localDescargaDtoToEntityRod = (d: LocalDescargaDto): CatLocalDescargaRod => ({
  id: d.id,
  projetoId: d.projetoId ?? null,
  nome: d.nome,
  deposito: d.deposito ?? null,
})
export const localDescargaDtoToEntityUrb = (d: LocalDescargaDto): CatLocalDescargaUrb => ({
  id: d.id,
  projetoId: d.projetoId ?? null,
  nome: d.nome,
  deposito: d.deposito ?? null,
  rua: d.rua ?? null,
  bairro: d.bairro ?? null,
})

export const projetoServicoDtoToEntity = (d: ProjetoServicoDto): CatProjetoServico => ({
  projetoId: d.projetoId,
  servicoId: d.servicoId,
  prioridade: d.prioridade,
})

export const equipeEncarregadoDtoToEntity = (d: EquipeEncarregadoDto): CatEquipeEncarregado => ({
  projetoId: d.projetoId,
  equipeId: d.equipeId,
  equipeNome: d.equipeNome,
  encarregadoId: d.encarregadoId,
  encarregadoNome: d.encarregadoNome,
})
